import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import GUI from 'lil-gui'
import earthVertexShader from './shaders/earth/vertex.glsl'
import earthFragmentShader from './shaders/earth/fragment.glsl'
import atmosphereVertexShader from './shaders/atmosphere/vertex.glsl'
import atmosphereFragmentShader from './shaders/atmosphere/fragment.glsl'
import { color, texture, uniform } from 'three/tsl'
import { MeshBasicMaterial } from 'three/webgpu'
import { planets } from './planetsData.js'
import { createPlanetInfobox, createPlanetSelector } from './createPlanetSelector.js'
import { loadNewPlanet} from './loadNewPlanet.js'
import { Lensflare, LensflareElement } from 'three/addons/objects/Lensflare.js';

/**
 * Base
 */
// Debug
const gui = new GUI()
gui.hide()

// Canvas
const canvas = document.querySelector('canvas.webgl')

// Scene
const scene = new THREE.Scene()

// Loaders
const textureLoader = new THREE.TextureLoader()



// Background
const milkyWayTexture = textureLoader.load(
  'milkyway/2k_stars_milky_way.jpg',
  (texture) => {
    texture.minFilter = THREE.LinearFilter;
    scene.background = texture;

    updateBackgroundAspect();
  }
)
milkyWayTexture.colorSpace = THREE.SRGBColorSpace
milkyWayTexture.anisotropy = 8

// selected Planet
let currentPlanet = 'earth'
const ringMeshHolder = { mesh: null };

// Easing function
function easeInOutCubic(t) {
  return t < 0.5
    ? 4 * t * t * t
    : 1 - Math.pow(-2 * t + 2, 3) / 2
}

// Fade state
let isFading = false
let fadeDirection = null
let targetPlanet = null
let fadeProgress = 0
const fadeDuration = 0.4
let fadeStartTime = 0

// create thumbs
createPlanetSelector()

// create Info box
createPlanetInfobox(currentPlanet)

/**
 * Earth
 */
const planetParameters = {}
planetParameters.atmosphereDayColor = planets[currentPlanet].atmosphereDayColor
planetParameters.atmosphereTwilightColor = planets[currentPlanet].atmosphereTwilightColor

gui
  .addColor(planetParameters, 'atmosphereDayColor')
  .onChange(() =>
  {
    earthMaterial.uniforms.uAtmosphereDayColor.value.set(planetParameters.atmosphereDayColor)
    atmosphereMaterial.uniforms.uAtmosphereDayColor.value.set(planetParameters.atmosphereDayColor)
  })

gui
.addColor(planetParameters, 'atmosphereTwilightColor')
.onChange(() =>
{
  earthMaterial.uniforms.uAtmosphereTwilightColor.value.set(planetParameters.atmosphereTwilightColor)
  atmosphereMaterial.uniforms.uAtmosphereTwilightColor.value.set(planetParameters.atmosphereTwilightColor)
})


//Textures
const planet = planets[currentPlanet]
const planetDayTexture = textureLoader.load(planet.dayTexture)
planetDayTexture.colorSpace = THREE.SRGBColorSpace
planetDayTexture.anisotropy = 8

const planetNightTexture = textureLoader.load(planet.nightTexture || planet.dayTexture)
planetNightTexture.colorSpace = THREE.SRGBColorSpace
planetNightTexture.anisotropy = 8

const planetSpecularCloudsTexture = textureLoader.load(planet.specularTexture || planet.dayTexture)
planetSpecularCloudsTexture.anisotropy = 8

// Mesh
const earthGeometry = new THREE.SphereGeometry(planets[currentPlanet].scale, 64, 64)
console.log(planets[currentPlanet].scale, 64, 64);

const earthMaterial = new THREE.ShaderMaterial({
    vertexShader: earthVertexShader,
    fragmentShader: earthFragmentShader,
    transparent: true,
    uniforms:
    {
      uDayTexture: new THREE.Uniform(planetDayTexture),
      uNightTexture: new THREE.Uniform(planetNightTexture),
      uSpecularCloudsTexture: new THREE.Uniform(planetSpecularCloudsTexture),
      uSunDirection: new THREE.Uniform(new THREE.Vector3(0, 0, 1)),
      uAtmosphereDayColor: new THREE.Uniform(new THREE.Color(planetParameters.atmosphereDayColor)),
      uAtmosphereTwilightColor: new THREE.Uniform(new THREE.Color(planetParameters.atmosphereTwilightColor)),
      uOpacity: new THREE.Uniform(1.0)
    }
})
const earth = new THREE.Mesh(earthGeometry, earthMaterial)
earth.position.y = -0.4
scene.add(earth)

// Atmosphere
const atmosphereMaterial = new THREE.ShaderMaterial({
  vertexShader: atmosphereVertexShader,
  fragmentShader: atmosphereFragmentShader,
  uniforms:
  {
    uSunDirection: new THREE.Uniform(new THREE.Vector3(0, 0, 1)),
    uAtmosphereDayColor: new THREE.Uniform(new THREE.Color(planetParameters.atmosphereDayColor)),
    uAtmosphereTwilightColor: new THREE.Uniform(new THREE.Color(planetParameters.atmosphereTwilightColor)),
    uOpacity: new THREE.Uniform(1.0),
  },
  side: THREE.BackSide,
  transparent: true,
  blending: THREE.AdditiveBlending,
  depthWrite: false,
})
const atmosphere = new THREE.Mesh(earthGeometry, atmosphereMaterial)
atmosphere.scale.set(planets[currentPlanet].atmosphereScale, planets[currentPlanet].atmosphereScale, planets[currentPlanet].atmosphereScale)
atmosphere.position.y = -0.4
scene.add(atmosphere)

function switchPlanet(planetKey) {
  if (isFading || planetKey === currentPlanet) return

  isFading = true
  fadeDirection= 'out'
  targetPlanet = planetKey
  fadeStartTime = clock.getElapsedTime()
  createPlanetInfobox(targetPlanet)
}

window.switchPlanet = switchPlanet


/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight,
    pixelRatio: Math.min(window.devicePixelRatio, 2)
}

function updateBackgroundAspect() {
  let imageAspect
  let screenAspect

  if (scene.background) {
      const backgroundTexture = scene.background;

      const imageAspect = texture.image.width / texture.image.height
      const screenAspect = sizes.width / sizes.height
  }
  if (imageAspect / screenAspect > 1) {
      scene.background.offset.set( (imageAspect / screenAspect - 1) / 2, 0 )
      scene.background.repeat.set( screenAspect / imageAspect, 1 )
  }
  else {
      scene.background.offset.set( 0, (1 - screenAspect / imageAspect) / 2 )
      scene.background.repeat.set( 1, imageAspect / screenAspect )
  }

  scene.background.needsUpdate = true;
}

window.addEventListener('resize', () =>
{
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight
    sizes.pixelRatio = Math.min(window.devicePixelRatio, 2)

    // Update camera
    camera.aspect = sizes.width / sizes.height

    let newFOV = fov * sizes.height / sizes.width;
    // if (newFOV < 1) newFOV = 1;

    camera.fov = newFOV;
    camera.updateProjectionMatrix()

    updateBackgroundAspect()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(sizes.pixelRatio)

})

/**
 * Camera
 */
// Base camera

let fov

if(sizes.height > sizes.width) {
  fov = 25 * sizes.height / sizes.width;
} else {
  fov = 25;
}

const camera = new THREE.PerspectiveCamera(fov, sizes.width / sizes.height, 0.1, 100)
camera.position.x = 12
camera.position.y = 5
camera.position.z = 4
scene.add(camera)

// Controls
const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true

/**
 * Sun
 */
const sunSpherical = new THREE.Spherical(1, Math.PI * 0.5, 0.5)
const sunDirection = new THREE.Vector3()

// Debug
const debugSun = new THREE.Mesh(
  new THREE.IcosahedronGeometry(0.1, 2),
  new THREE.MeshBasicMaterial()
)

debugSun.visible = false;

scene.add(debugSun)

/**
 * Lensflare
 */

const textureFlare0  = textureLoader.load('lenses/lensflare0.png')
const textureFlare3 = textureLoader.load('lenses/lensflare1.png')

const flareLight = new THREE.PointLight(0xffffff, 0, 2000, 0);
scene.add(flareLight);

const lensflare = new Lensflare();
lensflare.addElement( new LensflareElement( textureFlare0, 700, 0, flareLight.color ) );
lensflare.addElement( new LensflareElement( textureFlare3, 60, 0.6 ) );
lensflare.addElement( new LensflareElement( textureFlare3, 70, 0.7 ) );
lensflare.addElement( new LensflareElement( textureFlare3, 120, 0.9 ) );
lensflare.addElement( new LensflareElement( textureFlare3, 70, 1 ) );
flareLight.add(lensflare);

flareLight.add(debugSun);

// Update
const SUN_DISTANCE = 50;

const updateSun = () =>
{
  // Sun direction
  sunDirection.setFromSpherical(sunSpherical)

  // Debug
  flareLight
    .position.copy(sunDirection)
    .multiplyScalar(SUN_DISTANCE)

  //Uniforms
  earthMaterial.uniforms.uSunDirection.value.copy(sunDirection)
  atmosphereMaterial.uniforms.uSunDirection.value.copy(sunDirection)
}

updateSun()

// Tweaks
gui
  .add(sunSpherical, 'phi')
  .min(0)
  .max(Math.PI)
  .onChange(updateSun)

  gui
  .add(sunSpherical, 'theta')
  .min(-Math.PI)
  .max(Math.PI)
  .onChange(updateSun)

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true
})
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(sizes.pixelRatio)
renderer.setClearColor('#000011')

/**
 * Animate
 */
const clock = new THREE.Clock()

const tick = () =>
{
    const elapsedTime = clock.getElapsedTime()


    // fading between planets
    if (isFading) {
      const timeSinceFadeStart = elapsedTime - fadeStartTime
      const progress = Math.min(timeSinceFadeStart / fadeDuration, 1.0)
      const easedProgress = easeInOutCubic(progress)

      if (fadeDirection === 'out')
        {
        const opacity = 1.0 - easedProgress

        earthMaterial.uniforms.uOpacity.value = opacity
        atmosphereMaterial.uniforms.uOpacity.value = opacity

        if (ringMeshHolder.mesh) {
          ringMeshHolder.mesh.material.opacity = opacity;
        }

        if (progress >= 1.0) {
          loadNewPlanet(
            targetPlanet,
            planets,
            textureLoader,
            earthMaterial,
            atmosphereMaterial,
            atmosphere,
            earth,
            planetParameters,
            scene,
            ringMeshHolder
            )

            currentPlanet = targetPlanet
            fadeDirection = 'in'
            fadeStartTime = elapsedTime
        }
        } else if (fadeDirection === 'in') {
          const opacity = easedProgress

          earthMaterial.uniforms.uOpacity.value = opacity
          atmosphereMaterial.uniforms.uOpacity.value = opacity

          if (ringMeshHolder.mesh) {
              ringMeshHolder.mesh.material.opacity = opacity;
          }

          if (progress >= 1) {
            fadeDirection = null
            isFading = false
            targetPlanet = null
          }
        }
      }

    earth.rotation.y = elapsedTime * 0.1 * planets[currentPlanet].rotationSpeed

    // Update controls
    controls.update()

    // Render
    renderer.render(scene, camera)

    // Call tick again on the next frame
    window.requestAnimationFrame(tick)
}

tick()
